"use client";

import { forwardRef, InputHTMLAttributes } from "react";
import { LucideIcon, AlertCircle, Info } from "lucide-react";
import { cn } from "@/lib/utils";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  labelClassName?: string;
  error?: string;
  helperText?: string;
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
  onRightIconClick?: () => void;
  rightIconAriaLabel?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      labelClassName,
      error,
      helperText,
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      onRightIconClick,
      rightIconAriaLabel,
      className = "",
      id,
      ...props
    },
    ref,
  ) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, "-");

    return (
      <div className="w-full group">
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              "block text-[13px] font-medium mb-2 tracking-[-0.01em]",
              error
                ? "text-error-light"
                : "text-foreground group-focus-within:text-brand",
              labelClassName,
            )}
          >
            {label}
          </label>
        )}

        <div
          className={cn(
            "relative rounded-[10px] transition-shadow duration-[220ms]",
            error
              ? "focus-within:[box-shadow:var(--input-error-ring)]"
              : "focus-within:[box-shadow:var(--input-focus-ring)]",
          )}
        >
          {LeftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-brand transition-colors">
              <LeftIcon className="w-4 h-4" />
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            className={cn(
              "w-full bg-input text-foreground placeholder:text-[var(--color-text-disabled)]",
              "border border-border rounded-[10px]",
              "px-3 py-2.5 text-sm tracking-[-0.005em]",
              "transition-[border-color] duration-[220ms]",
              "focus:outline-none focus:border-brand",
              "disabled:opacity-40 disabled:cursor-not-allowed",
              LeftIcon && "pl-[38px]",
              RightIcon && "pr-[44px]",
              error && "border-error-glow-30 focus:border-error",
              className,
            )}
            aria-invalid={error ? "true" : "false"}
            aria-describedby={
              error
                ? `${inputId}-error`
                : helperText
                  ? `${inputId}-helper`
                  : undefined
            }
            {...props}
          />

          {RightIcon && (
            <button
              type="button"
              onClick={onRightIconClick}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-brand-light hover:bg-brand-glow-08 transition-colors focus-ring"
              aria-label={rightIconAriaLabel || "Input action"}
            >
              <RightIcon className="w-4 h-4" aria-hidden />
            </button>
          )}
        </div>

        {error && (
          <p
            id={`${inputId}-error`}
            className="mt-2 text-xs text-error-light inline-flex items-center gap-1.5"
          >
            <AlertCircle className="w-3 h-3 shrink-0" aria-hidden />
            {error}
          </p>
        )}

        {helperText && !error && (
          <p
            id={`${inputId}-helper`}
            className="mt-2 text-xs text-muted-foreground inline-flex items-center gap-1.5"
          >
            <Info className="w-3 h-3 shrink-0" aria-hidden />
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

export { Input, type InputProps };
